import { FC } from 'react'

import { Link, useLocation } from '@remix-run/react'

import {
  IconMapPin,
  IconTrain,
  PrimaryButton,
  Link as ComponentLink,
} from '~/components'

type FacilityImageProps = {
  imageSrc: string
  facilityName: string
}

const FacilityImage: FC<FacilityImageProps> = ({ facilityName, imageSrc }) => {
  return (
    <div className="flex min-h-[180px] shrink-0">
      <img
        alt={`${facilityName}の画像`}
        className="h-auto w-full object-cover"
        src={imageSrc}
      />
    </div>
  )
}

type AddressInfoProps = {
  address: string
  buildingName: string
}

const AddressInfo: FC<AddressInfoProps> = ({ address, buildingName }) => {
  const addressWithBuilding = `${address}${buildingName ? ` ${buildingName}` : ''}`

  return (
    <>
      <div className="hidden min-w-0 flex-col justify-center md:flex">
        <p className="truncate text-body-s">{addressWithBuilding}</p>
      </div>
      <div className="flex min-w-0 flex-col justify-center md:hidden">
        <p className="truncate text-body-s">{address}</p>
        {!!buildingName && (
          <p className="truncate text-body-s">{buildingName}</p>
        )}
      </div>
    </>
  )
}

type OfficeInfoProps = {
  href: string
  facilityName: string
  address: string
  buildingName: string
  transportationInfo: string
  otherAccessInfo: string
}

const OfficeInfo: FC<OfficeInfoProps> = ({
  href,
  facilityName,
  address,
  buildingName,
  transportationInfo,
  otherAccessInfo,
}) => {
  return (
    <div>
      <ComponentLink href={href}>
        <p className="flex-wrap text-lg font-bold leading-[140%] text-link">
          {facilityName}
        </p>
      </ComponentLink>
      <div className="mt-4">
        <div className="flex flex-row items-center gap-x-1">
          <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[51px] bg-brand-bg">
            <IconMapPin className="h-[18px] w-[18px]" color="brand" />
          </div>
          <AddressInfo address={address} buildingName={buildingName} />
        </div>
        <div className="mt-2 flex flex-row items-center gap-x-1">
          <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[51px] bg-brand-bg">
            <IconTrain className="h-[18px] w-[18px]" color="brand" />
          </div>
          <p className="truncate text-body-s">
            {transportationInfo || otherAccessInfo
              ? `${transportationInfo} ${otherAccessInfo && `、${otherAccessInfo}`}`
              : '-'}
          </p>
        </div>
      </div>
    </div>
  )
}

type Space = {
  id: number
  types: string[]
  price: number | null
  priceTaxIncluded: number | null
  pricePrivateFlag: boolean
  capacity: number | null
}

type PlansDetailTableProps = {
  spaces: Space[]
}

const PlansDetailTable: FC<PlansDetailTableProps> = ({ spaces }) => {
  /**
   * NOTE:
   * 5つまでのプランを表示
   * タイプは2つまで表示する
   * 利用人数順で並べる
   * 利用人数がnullの場合は一番前に並べる
   * 地図画面のドロワーでも同じ処理があるので将来的に他の箇所でも利用頻度が増えたら共通化したい
   */
  const spacesToDisplay = spaces
    .slice(0, 5)
    .map((space) => {
      return { ...space, types: space.types.slice(0, 2) }
    })
    .sort((a, b) => {
      if (a.capacity === null) return -1
      if (b.capacity === null) return 1
      return a.capacity - b.capacity
    })
  const unDisplayedPlansCount = spaces.length > 5 ? spaces.length - 5 : 0

  return (
    <div className="flex flex-col overflow-hidden rounded">
      <div className="flex shrink-0 items-center justify-center bg-brand p-2 text-sm font-bold leading-normal text-white">
        プラン
      </div>
      <div className="w-full overflow-x-scroll rounded-b border border-brand-light bg-brand-bg px-2">
        <table className="w-full px-2">
          <thead className="bg-brand-bg text-xs leading-normal text-brand">
            <tr className="*:py-1">
              <th>タイプ</th>
              <th>賃料</th>
              <th>最大人数</th>
            </tr>
          </thead>
          <tbody>
            {spacesToDisplay.map((space) => (
              <tr
                className="border-t border-dashed border-brand-light"
                key={space.id}
              >
                <td>
                  <div className="flex min-w-[100px] flex-col items-center gap-1 py-1">
                    {space.types.map((type) => (
                      <p
                        className="w-full text-center text-[10px] font-bold text-brand"
                        key={type}
                      >
                        {type}
                      </p>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="my-2 ml-2 flex h-[49px] min-w-[114px] items-center justify-center rounded bg-white px-2 py-1">
                    {space.price === null ||
                    space.price === 0 ||
                    space.pricePrivateFlag ? (
                      <p className="text-body-bold text-cta">お問い合わせ</p>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-0.5">
                        <p className="text-lg font-bold leading-none text-cta">
                          {space.price.toLocaleString()}
                          <span className="ml-1 text-xs font-normal">
                            円/月
                          </span>
                        </p>
                        {space.priceTaxIncluded !== null &&
                          space.priceTaxIncluded !== 0 && (
                            <span className="text-[10px] leading-none text-grayText">
                              （税込{space.priceTaxIncluded.toLocaleString()}
                              円/月）
                            </span>
                          )}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="my-2 ml-2 flex h-[49px] min-w-[70px] items-center justify-center rounded bg-white px-2 py-1">
                    <p className="text-body-bold">
                      {space.capacity !== null ? (
                        <>
                          {space.capacity}
                          <span className="text-body-s font-normal">名</span>
                        </>
                      ) : (
                        '-'
                      )}
                    </p>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {unDisplayedPlansCount > 0 && (
          <div className="border-t border-dashed border-brand-light pb-2 pt-1 text-center">
            <p className="text-xs font-bold leading-[140%]">
              他
              <span className="text-base text-brand">
                {unDisplayedPlansCount}
              </span>
              件のプラン
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

type ButtonGroupProps = {
  contactLink: string
  officeDetailLink: string
}

const ButtonGroup: FC<ButtonGroupProps> = ({
  contactLink,
  officeDetailLink,
}) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  // NOTE: 地図画面に戻る際はofficeIdは必要ないので削除
  queryParams.delete('officeId')
  queryParams.set('mapVisited', 'true')
  const officeDetailLinkWithQueryParams = `${officeDetailLink}?${queryParams.toString()}`

  return (
    <div className="flex flex-row gap-x-4">
      <Link
        className="w-full"
        rel="noreferrer"
        target="_blank"
        to={contactLink}
      >
        <PrimaryButton className="h-[52px]" color="cta">
          お問い合わせ
        </PrimaryButton>
      </Link>
      <Link className="w-full" to={officeDetailLinkWithQueryParams}>
        <PrimaryButton className="h-[52px]" color="cta" outlined>
          詳細を見る
        </PrimaryButton>
      </Link>
    </div>
  )
}

type DrawerOfficeDetailCardAddressProps = {
  imageSrc: string
  facilityName: string
  officeDetailLink: string
  contactLink: string
  address: string
  buildingName: string
  transportationInfo: string
  otherAccessInfo: string
  spaces: Space[]
}

export const DrawerOfficeDetailCard: FC<DrawerOfficeDetailCardAddressProps> = ({
  imageSrc,
  facilityName,
  officeDetailLink,
  contactLink,
  address,
  buildingName,
  transportationInfo,
  otherAccessInfo,
  spaces,
}) => {
  return (
    <div>
      <div>
        <FacilityImage facilityName={facilityName} imageSrc={imageSrc} />
        <div className="mx-4 mt-4 min-w-0">
          <OfficeInfo
            address={address}
            buildingName={buildingName}
            facilityName={facilityName}
            href={officeDetailLink}
            otherAccessInfo={otherAccessInfo}
            transportationInfo={transportationInfo}
          />
          <div className="mt-4 hidden">
            <ButtonGroup
              contactLink={contactLink}
              officeDetailLink={officeDetailLink}
            />
          </div>
        </div>
      </div>
      <div className="mx-4 mt-4">
        <PlansDetailTable spaces={spaces} />
      </div>
      <div className="mx-4 mt-4">
        <ButtonGroup
          contactLink={contactLink}
          officeDetailLink={officeDetailLink}
        />
      </div>
    </div>
  )
}
